/**
 * Validation wrapper utility that integrates validation results with the application's error handling system
 */

import { AppError, ErrorType } from '@/utils/error/errorHandler';
// Separate type import for ValidationResult due to verbatimModuleSyntax
import type { ValidationResult } from './validation';
import { validateObject, validateUUID as validateUUIDUtil } from './validation';

/**
 * Processes validation results and throws an AppError if any validations fail
 * 
 * @param results An array of ValidationResult objects
 * @param context Additional context information to include in the error
 * @throws AppError with ErrorType.VALIDATION if any validations fail
 */
export function processValidationResults(results: ValidationResult[], context: Record<string, any> = {}): void {
  const failures = results.filter(result => !result.valid);
  
  if (failures.length > 0) {
    const messages = failures.map(failure => failure.message).filter(Boolean);
    
    // Extract detailed information about what failed for better debugging
    const validationDetails = failures.map(failure => ({
      message: failure.message || 'Validation failed with no message',
      valid: failure.valid
    }));
    
    console.warn('Validation failed:', JSON.stringify(validationDetails, null, 2));
    
    throw new AppError({
      message: `Validation failed: ${messages.join(', ')}`,
      type: ErrorType.VALIDATION,
      metadata: { 
        ...context, 
        validationErrors: validationDetails,
        failureCount: failures.length
      }
    });
  }
}

/**
 * Validates an object against a schema and throws an AppError if invalid
 * 
 * @param obj The object to validate
 * @param schema The validation schema
 * @param objName The name of the object being validated
 * @param context Additional context information to include in the error
 * @throws AppError with ErrorType.VALIDATION if the object is invalid
 */
export function validate(
  obj: any, 
  schema: Record<string, any>, 
  objName: string = 'object',
  context: Record<string, any> = {}
): void {
  try {
    const results = validateObject(obj, schema, objName);
    processValidationResults(results, {
      ...context,
      objectType: objName,
      providedFields: Object.keys(obj || {})
    });
  } catch (error) {
    // If it's not already an AppError, wrap it in one
    if (!(error instanceof AppError)) {
      throw new AppError({
        message: `Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: ErrorType.VALIDATION,
        cause: error,
        metadata: {
          ...context, 
          objectType: objName,
          providedFields: obj ? Object.keys(obj) : []
        }
      });
    }
    throw error;
  }
}

/**
 * Validates that a value is a valid UUID and throws an AppError if invalid
 * 
 * @param value The value to validate
 * @param fieldName The name of the field being validated
 * @param context Additional context information to include in the error
 * @throws AppError with ErrorType.VALIDATION if the value is not a valid UUID
 */
export function validateUUID(
  value: string, 
  fieldName: string, 
  context: Record<string, any> = {}
): void {
  const result = validateUUIDUtil(value, fieldName);
  if (!result.valid) {
    throw new AppError({
      message: result.message || `${fieldName} must be a valid UUID`,
      type: ErrorType.VALIDATION,
      metadata: { 
        ...context, 
        field: fieldName, 
        value: value ? value.substring(0, 36) : value,
        valueType: typeof value
      }
    });
  }
}

/**
 * Validates a string against basic criteria (e.g., non-empty) and throws an AppError if invalid
 * 
 * @param value The string value to validate
 * @param fieldName The name of the field being validated
 * @param options Options like minLength, maxLength, pattern (optional)
 * @param context Additional context information to include in the error
 * @throws AppError with ErrorType.VALIDATION if the value is not a valid string or violates options
 */
export function validateString(
  value: any, // Accept any initially to check type
  fieldName: string,
  options: { minLength?: number; maxLength?: number; pattern?: RegExp } = {},
  context: Record<string, any> = {}
): void {
  if (typeof value !== 'string') {
    throw new AppError({
      message: `${fieldName} must be a string`, 
      type: ErrorType.VALIDATION, 
      metadata: { 
        ...context, 
        field: fieldName, 
        value: String(value).substring(0, 50), // Truncate long values
        valueType: typeof value
      }
    });
  }

  const { minLength = 1, maxLength, pattern } = options; // Default minLength to 1

  if (minLength !== undefined && value.length < minLength) {
    throw new AppError({
      message: `${fieldName} must be at least ${minLength} character(s) long`, 
      type: ErrorType.VALIDATION, 
      metadata: { 
        ...context, 
        field: fieldName, 
        value: value.substring(0, 50),
        minLengthRequired: minLength, 
        actualLength: value.length
      }
    });
  }
  
  if (maxLength !== undefined && value.length > maxLength) {
    throw new AppError({
      message: `${fieldName} must be no more than ${maxLength} character(s) long`, 
      type: ErrorType.VALIDATION, 
      metadata: { 
        ...context, 
        field: fieldName, 
        value: value.substring(0, 50),
        maxLengthRequired: maxLength, 
        actualLength: value.length
      }
    });
  }
  
  if (pattern && !pattern.test(value)) {
    throw new AppError({
      message: `${fieldName} does not match the required pattern`, 
      type: ErrorType.VALIDATION, 
      metadata: { 
        ...context, 
        field: fieldName, 
        value: value.substring(0, 50),
        pattern: pattern.toString()
      }
    });
  }
} 